import React, { useState, useEffect } from 'react';
import { useJWTAuth } from '../auth/JWTAuthContext';
import AuthGuard from '../components/AuthGuard';

const BattleSimulator = () => {
  const [heroes, setHeroes] = useState([]);
  const [selectedHero1, setSelectedHero1] = useState(null);
  const [selectedHero2, setSelectedHero2] = useState(null);
  const [battleLog, setBattleLog] = useState([]);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isLoadingHeroes, setIsLoadingHeroes] = useState(false);
  const [heroesError, setHeroesError] = useState(null);
  const [tempStrategies1, setTempStrategies1] = useState({});
  const [tempStrategies2, setTempStrategies2] = useState({});
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [expandedHero1Info, setExpandedHero1Info] = useState(false);
  const [expandedHero1Strategies, setExpandedHero1Strategies] = useState(false);
  const [expandedHero2Info, setExpandedHero2Info] = useState(false);
  const [expandedHero2Strategies, setExpandedHero2Strategies] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState('instant'); // 'instant', 'custom', 'realtime'
  const [customDelay, setCustomDelay] = useState(1000); // milliseconds
  const [isPaused, setIsPaused] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const { getAuthHeaders, isAuthenticated, user } = useJWTAuth();

  // Load heroes when user is available
  useEffect(() => {
    if (user) {
      loadHeroes();
    }
  }, [user]);

  const loadHeroes = async () => {
    setIsLoadingHeroes(true);
    setHeroesError(null);
    try {
      const currentUserId = user?.id || '1';
      const response = await fetch(`/api/heroes/user/${currentUserId}`);
      const data = await response.json();
      if (data.success) {
        // Sort heroes by level (highest first)
        const sortedHeroes = data.data.sort((a, b) => (b.level || 0) - (a.level || 0));
        setHeroes(sortedHeroes);
      } else {
        setHeroesError(data.error || 'Failed to load heroes');
      }
    } catch (error) {
      console.error('Error loading heroes:', error);
      setHeroesError('Failed to load heroes');
    } finally {
      setIsLoadingHeroes(false);
    }
  };

  const loadHeroDetails = async (heroId) => {
    try {
      const response = await fetch(`/api/heroes/${heroId}`);
      const data = await response.json();
      return data.success ? data.data : null;
    } catch (error) {
      console.error('Error loading hero details:', error);
      return null;
    }
  };

  const simulateBattle = async () => {
    if (!selectedHero1 || !selectedHero2) return;

    setIsSimulating(true);
    setBattleLog([]);
    setExpandedEvents(new Set());
    setCurrentEventIndex(0);
    setIsPaused(false);

    try {
      // Load detailed hero data
      const [hero1Data, hero2Data] = await Promise.all([
        loadHeroDetails(selectedHero1.id),
        loadHeroDetails(selectedHero2.id)
      ]);

      if (!hero1Data || !hero2Data) {
        alert('Error loading hero details');
        setIsSimulating(false);
        return;
      }

      // Create battle simulation
      const simulation = new BattleSimulation(hero1Data, hero2Data, tempStrategies1, tempStrategies2);
      
      if (simulationSpeed === 'instant') {
        // Run instantly
        const log = simulation.runBattle();
        setBattleLog(log);
        setIsSimulating(false);
      } else {
        // Run with timing
        await runTimedBattle(simulation);
      }
    } catch (error) {
      console.error('Error simulating battle:', error);
      alert('Error simulating battle');
      setIsSimulating(false);
    }
  };

  const runTimedBattle = async (simulation) => {
    if (simulationSpeed === 'realtime') {
      await runRealtimeBattle(simulation);
    } else {
      await runTurnBasedBattle(simulation);
    }
  };

  const runTurnBasedBattle = async (simulation) => {
    const events = [];
    let currentTurn = 0;
    
    // Override the logEvent method to collect events
    const originalLogEvent = simulation.logEvent.bind(simulation);
    simulation.logEvent = (summary, details) => {
      const event = {
        timestamp: `Turn ${currentTurn}`,
        summary,
        details
      };
      events.push(event);
    };

    // Run the battle and collect all events
    simulation.runBattle();
    
    // Restore original method
    simulation.logEvent = originalLogEvent;
    
    // Display events with timing
    setBattleLog([]);
    setCurrentEventIndex(0);
    
    for (let i = 0; i < events.length; i++) {
      // Wait for pause to be released
      while (isPaused) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setCurrentEventIndex(i);
      setBattleLog(prev => [...prev, events[i]]);
      
      // Use custom delay for turn-based mode
      if (customDelay > 0) {
        await new Promise(resolve => setTimeout(resolve, customDelay));
      }
    }
    
    setIsSimulating(false);
  };

  const runRealtimeBattle = async (simulation) => {
    // Initialize real-time battle state - create fresh copies
    const hero1 = {
      ...simulation.hero1,
      health: simulation.hero1.maxHealth,
      isAlive: true,
      attacksAttempted: 0,
      attacksHit: 0,
      attacksMissed: 0,
      criticalHits: 0,
      damageDealt: 0,
      damageTaken: 0,
      eliminations: 0
    };
    const hero2 = {
      ...simulation.hero2,
      health: simulation.hero2.maxHealth,
      isAlive: true,
      attacksAttempted: 0,
      attacksHit: 0,
      attacksMissed: 0,
      criticalHits: 0,
      damageDealt: 0,
      damageTaken: 0,
      eliminations: 0
    };
    
    // Set up attack timers
    hero1.nextAttackTime = 0; // Can attack immediately
    hero2.nextAttackTime = 0; // Can attack immediately
    
    const events = [];
    let battleTime = 0;
    const maxBattleTime = 60000; // 60 seconds max
    
    // Override the logEvent method to collect events
    const originalLogEvent = simulation.logEvent.bind(simulation);
    simulation.logEvent = (summary, details) => {
      const event = {
        timestamp: `${(battleTime / 1000).toFixed(1)}s`,
        summary,
        details
      };
      events.push(event);
      
      // Update display immediately
      setCurrentEventIndex(events.length - 1);
      setBattleLog([...events]);
    };

    // Log battle start
    simulation.logEvent(
      `Real-time battle begins: ${hero1.name} vs ${hero2.name}`,
      `Each hero will attack based on their individual attack cooldowns`
    );

    // Display events with timing
    setBattleLog([]);
    setCurrentEventIndex(0);
    
    // Real-time battle loop
    while (hero1.isAlive && hero2.isAlive && battleTime < maxBattleTime) {
      // Wait for pause to be released
      while (isPaused) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      try {
        // Find the next attack to happen
        const nextAttackTime = Math.min(hero1.nextAttackTime, hero2.nextAttackTime);
        
        // Wait until the next attack time
        if (nextAttackTime > battleTime) {
          const waitTime = nextAttackTime - battleTime;
          // Use actual millisecond timing for true real-time battles
          await new Promise(resolve => setTimeout(resolve, waitTime));
          battleTime = nextAttackTime;
        }
        
        // Process the attack(s) that happen at this time
        if (hero1.nextAttackTime <= battleTime && hero1.isAlive && hero2.isAlive) {
          const cooldownInfo = simulation.getAttackCooldownMs(hero1);
          simulation.processAttack(hero1, hero2, cooldownInfo);
          hero1.nextAttackTime = battleTime + cooldownInfo.cooldown;
        }
        
        if (hero2.nextAttackTime <= battleTime && hero2.isAlive && hero1.isAlive) {
          const cooldownInfo = simulation.getAttackCooldownMs(hero2);
          simulation.processAttack(hero2, hero1, cooldownInfo);
          hero2.nextAttackTime = battleTime + cooldownInfo.cooldown;
        }
        
        // Small delay to prevent overwhelming the UI
        await new Promise(resolve => setTimeout(resolve, 10));
      } catch (error) {
        console.error('Error in real-time battle:', error);
        simulation.logEvent(
          `Battle error at ${(battleTime / 1000).toFixed(1)}s`,
          `Error: ${error.message}`
        );
        break;
      }
    }

    // Battle conclusion
    if (hero1.isAlive && hero2.isAlive) {
      simulation.logEvent(
        `Battle ended in a draw after ${(battleTime / 1000).toFixed(1)} seconds`,
        `Both heroes survived the maximum battle time`
      );
    } else if (hero1.isAlive) {
      simulation.logEvent(
        `${hero1.name} wins!`,
        `Final Health: ${hero1.health.toFixed(1)}/${hero1.maxHealth}\nDamage Dealt: ${hero1.damageDealt.toFixed(1)}\nDamage Taken: ${hero1.damageTaken.toFixed(1)}`
      );
    } else {
      simulation.logEvent(
        `${hero2.name} wins!`,
        `Final Health: ${hero2.health.toFixed(1)}/${hero2.maxHealth}\nDamage Dealt: ${hero2.damageDealt.toFixed(1)}\nDamage Taken: ${hero2.damageTaken.toFixed(1)}`
      );
    }

    // Update the original simulation heroes with the final stats from real-time battle
    simulation.hero1.attacksAttempted = hero1.attacksAttempted;
    simulation.hero1.attacksHit = hero1.attacksHit;
    simulation.hero1.attacksMissed = hero1.attacksMissed;
    simulation.hero1.criticalHits = hero1.criticalHits;
    simulation.hero1.damageDealt = hero1.damageDealt;
    simulation.hero1.damageTaken = hero1.damageTaken;
    simulation.hero1.eliminations = hero1.eliminations;
    simulation.hero1.health = hero1.health;
    simulation.hero1.isAlive = hero1.isAlive;

    simulation.hero2.attacksAttempted = hero2.attacksAttempted;
    simulation.hero2.attacksHit = hero2.attacksHit;
    simulation.hero2.attacksMissed = hero2.attacksMissed;
    simulation.hero2.criticalHits = hero2.criticalHits;
    simulation.hero2.damageDealt = hero2.damageDealt;
    simulation.hero2.damageTaken = hero2.damageTaken;
    simulation.hero2.eliminations = hero2.eliminations;
    simulation.hero2.health = hero2.health;
    simulation.hero2.isAlive = hero2.isAlive;

    // Add final stats summary
    const battleDurationSeconds = battleTime / 1000;
    simulation.logEvent(
      "=== FINAL BATTLE STATISTICS ===",
      simulation.generateFinalStats(battleDurationSeconds)
    );
    
    // Restore original method
    simulation.logEvent = originalLogEvent;
    
    setIsSimulating(false);
  };

  const pauseSimulation = () => {
    setIsPaused(!isPaused);
  };

  const stopSimulation = () => {
    setIsSimulating(false);
    setIsPaused(false);
  };

  const toggleEventExpansion = (eventIndex) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventIndex)) {
      newExpanded.delete(eventIndex);
    } else {
      newExpanded.add(eventIndex);
    }
    setExpandedEvents(newExpanded);
  };

  const updateTempStrategy = (heroNum, strategy, value) => {
    if (heroNum === 1) {
      setTempStrategies1(prev => ({ ...prev, [strategy]: value }));
    } else {
      setTempStrategies2(prev => ({ ...prev, [strategy]: value }));
    }
  };

  // Aggression calculation methods
  const getAggressionDamageBonus = (aggression) => {
    // High aggression (80-100): +20% damage bonus
    // Medium aggression (40-60): 0% bonus
    // Low aggression (0-20): -10% damage penalty
    if (aggression >= 80) return (aggression - 80) * 1.0; // 0-20% bonus
    if (aggression <= 20) return (aggression - 20) * 0.5; // -10% to 0% penalty
    return 0; // 40-60: no change
  };

  const getAggressionCritBonus = (aggression) => {
    // High aggression (80-100): +5% crit chance
    // Medium aggression (40-60): 0% bonus
    // Low aggression (0-20): -2% crit penalty
    if (aggression >= 80) return (aggression - 80) * 0.25; // 0-5% bonus
    if (aggression <= 20) return (aggression - 20) * 0.1; // -2% to 0% penalty
    return 0; // 40-60: no change
  };

  const getAggressionEvasionBonus = (aggression) => {
    // High aggression (80-100): -5% evasion penalty
    // Medium aggression (40-60): 0% change
    // Low aggression (0-20): +5% evasion bonus
    if (aggression >= 80) return (aggression - 80) * -0.25; // 0 to -5% penalty
    if (aggression <= 20) return (20 - aggression) * 0.25; // 0 to +5% bonus
    return 0; // 40-60: no change
  };

  const getAggressionCooldownModifier = (aggression) => {
    // High aggression (80-100): -200ms faster attacks
    // Medium aggression (40-60): 0ms change
    // Low aggression (0-20): +200ms slower attacks
    if (aggression >= 80) return (aggression - 80) * -10; // 0 to -200ms
    if (aggression <= 20) return (20 - aggression) * 10; // 0 to +200ms
    return 0; // 40-60: no change
  };

  const getAggressionDefensePenalty = (aggression) => {
    // High aggression (80-100): -10% defense penalty
    // Medium aggression (40-60): 0% change
    // Low aggression (0-20): +10% defense bonus
    if (aggression >= 80) return (aggression - 80) * -0.5; // 0 to -10% penalty
    if (aggression <= 20) return (20 - aggression) * 0.5; // 0 to +10% bonus
    return 0; // 40-60: no change
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">Battle Simulator</h1>
        
        {isLoadingHeroes && (
          <div className="text-center py-8">
            <div className="text-gray-400">Loading heroes...</div>
          </div>
        )}

        {heroesError && (
          <div className="text-center py-8">
            <div className="text-red-400 mb-4">Error: {heroesError}</div>
            <button 
              onClick={loadHeroes}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoadingHeroes && !heroesError && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Hero 1 Selection */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Hero 1</h2>
              <select 
                className="w-full p-3 bg-gray-700 rounded mb-4"
                value={selectedHero1?.id || ''}
                onChange={(e) => {
                  const hero = heroes.find(h => h.id === e.target.value);
                  setSelectedHero1(hero);
                  setTempStrategies1({});
                }}
              >
                <option value="">Select Hero 1</option>
                {heroes.map(hero => (
                  <option key={hero.id} value={hero.id}>
                    {hero.name} (Level {hero.level || 1})
                  </option>
                ))}
              </select>

              {selectedHero1 && (
                <div className="space-y-4">
                  <div className="bg-gray-700 p-4 rounded">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedHero1Info(!expandedHero1Info)}
                    >
                      <h3 className="font-semibold">Hero Info</h3>
                      <span className="text-lg">{expandedHero1Info ? '−' : '+'}</span>
                    </div>
                    {expandedHero1Info && (
                      <div className="mt-3">
                        <div className="mb-2 text-sm">
                          <div>Level: {selectedHero1.level || 1}</div>
                          <div>Total XP: {selectedHero1.total_xp || 0}</div>
                        </div>
                        <h4 className="font-semibold mb-2">Primary Attributes</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>Strength: {selectedHero1.strength}</div>
                          <div>Endurance: {selectedHero1.endurance}</div>
                          <div>Coordination: {selectedHero1.coordination}</div>
                          <div>Quickness: {selectedHero1.quickness}</div>
                          <div>Focus: {selectedHero1.focus}</div>
                          <div>Self: {selectedHero1.self}</div>
                        </div>
                        <h4 className="font-semibold mb-2 mt-3">Calculated Stats</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div title="Base: 200 + (Endurance × 2) + (Strength × 1) = 200 + ({selectedHero1.endurance} × 2) + ({selectedHero1.strength} × 1) = {200 + (selectedHero1.endurance * 2) + (selectedHero1.strength * 1)}">
                            Health: {200 + (selectedHero1.endurance * 2) + (selectedHero1.strength * 1)}
                          </div>
                          <div title="Base: 20 + (Quickness × 0.5) = 20 + ({selectedHero1.quickness} × 0.5) = {(20 + (selectedHero1.quickness * 0.5)).toFixed(1)}">
                            Move Speed: {(20 + (selectedHero1.quickness * 0.5)).toFixed(1)}
                          </div>
                          <div title="(Coordination - 10) × 1% = ({selectedHero1.coordination} - 10) × 1% = {((selectedHero1.coordination - 10) * 1).toFixed(1)}%">
                            Accuracy: {((selectedHero1.coordination - 10) * 1).toFixed(1)}%
                          </div>
                          <div title="Base: (Quickness - 10) × 0.8% + Aggression Bonus = ({selectedHero1.quickness} - 10) × 0.8% + {getAggressionEvasionBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50).toFixed(1)}% = {(((selectedHero1.quickness - 10) * 0.8) + getAggressionEvasionBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(1)}%">
                            Evasion: {(((selectedHero1.quickness - 10) * 0.8) + getAggressionEvasionBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(1)}%
                          </div>
                          <div title="Base: 5% + (Focus - 10) × 0.3% + Aggression Bonus = 5% + ({selectedHero1.focus} - 10) × 0.3% + {getAggressionCritBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50).toFixed(1)}% = {(5 + (selectedHero1.focus - 10) * 0.3 + getAggressionCritBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(1)}%">
                            Crit Chance: {(5 + (selectedHero1.focus - 10) * 0.3 + getAggressionCritBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(1)}%
                          </div>
                          <div title="Base: (Strength - 10) × 0.5% + Aggression Bonus = ({selectedHero1.strength} - 10) × 0.5% + {getAggressionDamageBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50).toFixed(1)}% = {(((selectedHero1.strength - 10) * 0.5) + getAggressionDamageBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(1)}%">
                            Damage Bonus: {(((selectedHero1.strength - 10) * 0.5) + getAggressionDamageBonus(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(1)}%
                          </div>
                          <div title="Base: 2000ms ÷ 1.0 + Aggression Modifier = 2000ms + {getAggressionCooldownModifier(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50).toFixed(0)}ms = {(2000 + getAggressionCooldownModifier(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(0)}ms">
                            Attack Cooldown: {(2000 + getAggressionCooldownModifier(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50)).toFixed(0)}ms
                          </div>
                          <div title="Base: 0% + Aggression Penalty = 0% + {getAggressionDefensePenalty(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50).toFixed(1)}% = {getAggressionDefensePenalty(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50).toFixed(1)}%">
                            Defense Penalty: {getAggressionDefensePenalty(tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-700 p-4 rounded">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedHero1Strategies(!expandedHero1Strategies)}
                    >
                      <h3 className="font-semibold">Strategy Overrides</h3>
                      <span className="text-lg">{expandedHero1Strategies ? '−' : '+'}</span>
                    </div>
                    {expandedHero1Strategies && (
                      <div className="mt-3">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm mb-1">Aggression (0-100)</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50}
                              onChange={(e) => updateTempStrategy(1, 'strategy_aggression', parseInt(e.target.value))}
                              className="w-full"
                            />
                            <div className="text-sm text-gray-400">
                              {tempStrategies1.strategy_aggression ?? selectedHero1.strategy_aggression ?? 50}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm mb-1">Melee vs Ranged (0-100)</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={tempStrategies1.strategy_melee_ranged ?? selectedHero1.strategy_melee_ranged ?? 50}
                              onChange={(e) => updateTempStrategy(1, 'strategy_melee_ranged', parseInt(e.target.value))}
                              className="w-full"
                            />
                            <div className="text-sm text-gray-400">
                              {tempStrategies1.strategy_melee_ranged ?? selectedHero1.strategy_melee_ranged ?? 50}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm mb-1">Item Use (0-100)</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={tempStrategies1.strategy_item_use ?? selectedHero1.strategy_item_use ?? 50}
                              onChange={(e) => updateTempStrategy(1, 'strategy_item_use', parseInt(e.target.value))}
                              className="w-full"
                            />
                            <div className="text-sm text-gray-400">
                              {tempStrategies1.strategy_item_use ?? selectedHero1.strategy_item_use ?? 50}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Hero 2 Selection */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">Hero 2</h2>
              <select 
                className="w-full p-3 bg-gray-700 rounded mb-4"
                value={selectedHero2?.id || ''}
                onChange={(e) => {
                  const hero = heroes.find(h => h.id === e.target.value);
                  setSelectedHero2(hero);
                  setTempStrategies2({});
                }}
              >
                <option value="">Select Hero 2</option>
                {heroes.map(hero => (
                  <option key={hero.id} value={hero.id}>
                    {hero.name} (Level {hero.level || 1})
                  </option>
                ))}
              </select>

              {selectedHero2 && (
                <div className="space-y-4">
                  <div className="bg-gray-700 p-4 rounded">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedHero2Info(!expandedHero2Info)}
                    >
                      <h3 className="font-semibold">Hero Info</h3>
                      <span className="text-lg">{expandedHero2Info ? '−' : '+'}</span>
                    </div>
                    {expandedHero2Info && (
                      <div className="mt-3">
                        <div className="mb-2 text-sm">
                          <div>Level: {selectedHero2.level || 1}</div>
                          <div>Total XP: {selectedHero2.total_xp || 0}</div>
                        </div>
                        <h4 className="font-semibold mb-2">Primary Attributes</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>Strength: {selectedHero2.strength}</div>
                          <div>Endurance: {selectedHero2.endurance}</div>
                          <div>Coordination: {selectedHero2.coordination}</div>
                          <div>Quickness: {selectedHero2.quickness}</div>
                          <div>Focus: {selectedHero2.focus}</div>
                          <div>Self: {selectedHero2.self}</div>
                        </div>
                        <h4 className="font-semibold mb-2 mt-3">Calculated Stats</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div title="Base: 200 + (Endurance × 2) + (Strength × 1) = 200 + ({selectedHero2.endurance} × 2) + ({selectedHero2.strength} × 1) = {200 + (selectedHero2.endurance * 2) + (selectedHero2.strength * 1)}">
                            Health: {200 + (selectedHero2.endurance * 2) + (selectedHero2.strength * 1)}
                          </div>
                          <div title="Base: 20 + (Quickness × 0.5) = 20 + ({selectedHero2.quickness} × 0.5) = {(20 + (selectedHero2.quickness * 0.5)).toFixed(1)}">
                            Move Speed: {(20 + (selectedHero2.quickness * 0.5)).toFixed(1)}
                          </div>
                          <div title="(Coordination - 10) × 1% = ({selectedHero2.coordination} - 10) × 1% = {((selectedHero2.coordination - 10) * 1).toFixed(1)}%">
                            Accuracy: {((selectedHero2.coordination - 10) * 1).toFixed(1)}%
                          </div>
                          <div title="Base: (Quickness - 10) × 0.8% + Aggression Bonus = ({selectedHero2.quickness} - 10) × 0.8% + {getAggressionEvasionBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50).toFixed(1)}% = {(((selectedHero2.quickness - 10) * 0.8) + getAggressionEvasionBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(1)}%">
                            Evasion: {(((selectedHero2.quickness - 10) * 0.8) + getAggressionEvasionBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(1)}%
                          </div>
                          <div title="Base: 5% + (Focus - 10) × 0.3% + Aggression Bonus = 5% + ({selectedHero2.focus} - 10) × 0.3% + {getAggressionCritBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50).toFixed(1)}% = {(5 + (selectedHero2.focus - 10) * 0.3 + getAggressionCritBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(1)}%">
                            Crit Chance: {(5 + (selectedHero2.focus - 10) * 0.3 + getAggressionCritBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(1)}%
                          </div>
                          <div title="Base: (Strength - 10) × 0.5% + Aggression Bonus = ({selectedHero2.strength} - 10) × 0.5% + {getAggressionDamageBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50).toFixed(1)}% = {(((selectedHero2.strength - 10) * 0.5) + getAggressionDamageBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(1)}%">
                            Damage Bonus: {(((selectedHero2.strength - 10) * 0.5) + getAggressionDamageBonus(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(1)}%
                          </div>
                          <div title="Base: 2000ms ÷ 1.0 + Aggression Modifier = 2000ms + {getAggressionCooldownModifier(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50).toFixed(0)}ms = {(2000 + getAggressionCooldownModifier(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(0)}ms">
                            Attack Cooldown: {(2000 + getAggressionCooldownModifier(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50)).toFixed(0)}ms
                          </div>
                          <div title="Base: 0% + Aggression Penalty = 0% + {getAggressionDefensePenalty(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50).toFixed(1)}% = {getAggressionDefensePenalty(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50).toFixed(1)}%">
                            Defense Penalty: {getAggressionDefensePenalty(tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-700 p-4 rounded">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => setExpandedHero2Strategies(!expandedHero2Strategies)}
                    >
                      <h3 className="font-semibold">Strategy Overrides</h3>
                      <span className="text-lg">{expandedHero2Strategies ? '−' : '+'}</span>
                    </div>
                    {expandedHero2Strategies && (
                      <div className="mt-3">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm mb-1">Aggression (0-100)</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50}
                              onChange={(e) => updateTempStrategy(2, 'strategy_aggression', parseInt(e.target.value))}
                              className="w-full"
                            />
                            <div className="text-sm text-gray-400">
                              {tempStrategies2.strategy_aggression ?? selectedHero2.strategy_aggression ?? 50}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm mb-1">Melee vs Ranged (0-100)</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={tempStrategies2.strategy_melee_ranged ?? selectedHero2.strategy_melee_ranged ?? 50}
                              onChange={(e) => updateTempStrategy(2, 'strategy_melee_ranged', parseInt(e.target.value))}
                              className="w-full"
                            />
                            <div className="text-sm text-gray-400">
                              {tempStrategies2.strategy_melee_ranged ?? selectedHero2.strategy_melee_ranged ?? 50}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm mb-1">Item Use (0-100)</label>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={tempStrategies2.strategy_item_use ?? selectedHero2.strategy_item_use ?? 50}
                              onChange={(e) => updateTempStrategy(2, 'strategy_item_use', parseInt(e.target.value))}
                              className="w-full"
                            />
                            <div className="text-sm text-gray-400">
                              {tempStrategies2.strategy_item_use ?? selectedHero2.strategy_item_use ?? 50}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Battle Controls */}
        <div className="mb-8 space-y-4">
          {/* Simulation Speed Controls */}
          <div className="bg-gray-800 p-4 rounded">
            <h3 className="text-lg font-semibold mb-3">Simulation Speed</h3>
            <div className="space-y-3">
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="simulationSpeed"
                    value="instant"
                    checked={simulationSpeed === 'instant'}
                    onChange={(e) => setSimulationSpeed(e.target.value)}
                    className="mr-2"
                    disabled={isSimulating}
                  />
                  Instant
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="simulationSpeed"
                    value="custom"
                    checked={simulationSpeed === 'custom'}
                    onChange={(e) => setSimulationSpeed(e.target.value)}
                    className="mr-2"
                    disabled={isSimulating}
                  />
                  Turn-based with Delay
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="simulationSpeed"
                    value="realtime"
                    checked={simulationSpeed === 'realtime'}
                    onChange={(e) => setSimulationSpeed(e.target.value)}
                    className="mr-2"
                    disabled={isSimulating}
                  />
                  Real-time
                </label>
              </div>
              
              {simulationSpeed === 'custom' && (
                <div className="flex items-center gap-3">
                  <label className="text-sm">Delay between turns:</label>
                  <input
                    type="range"
                    min="100"
                    max="10000"
                    step="100"
                    value={customDelay}
                    onChange={(e) => setCustomDelay(parseInt(e.target.value))}
                    className="flex-1"
                    disabled={isSimulating}
                  />
                  <span className="text-sm w-20">{customDelay}ms</span>
                </div>
              )}
              
              {simulationSpeed === 'realtime' && (
                <div className="text-sm text-gray-400">
                  Each hero attacks based on their individual attack cooldowns in real-time. Battles will play out as if you're watching live combat - faster heroes can attack multiple times before slower heroes attack once.
                </div>
              )}
            </div>
          </div>

          {/* Battle Controls */}
          <div className="text-center">
            <div className="flex gap-4 justify-center">
              <button
                onClick={simulateBattle}
                disabled={!selectedHero1 || !selectedHero2 || isSimulating}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-8 py-3 rounded-lg font-semibold"
              >
                {isSimulating ? 'Simulating Battle...' : 'Start Battle'}
              </button>
              
              {isSimulating && (
                <>
                  <button
                    onClick={pauseSimulation}
                    className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded font-semibold"
                  >
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    onClick={stopSimulation}
                    className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded font-semibold"
                  >
                    Stop
                  </button>
                </>
              )}
            </div>
            
            {isSimulating && (
              <div className="text-sm text-gray-400 mt-2">
                {isPaused ? 'Simulation paused' : `Playing event ${currentEventIndex + 1} of battle...`}
              </div>
            )}
          </div>
        </div>

        {/* Battle Log */}
        {battleLog.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Battle Log</h2>
            <div className="space-y-2">
              {battleLog.map((event, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                  <div 
                    className="cursor-pointer hover:bg-gray-700 p-2 rounded"
                    onClick={() => toggleEventExpansion(index)}
                  >
                    <div className="font-semibold">{event.summary}</div>
                    <div className="text-sm text-gray-400">{event.timestamp}</div>
                  </div>
                  
                  {expandedEvents.has(index) && (
                    <div className="mt-2 p-3 bg-gray-700 rounded text-sm">
                      <div className="font-semibold mb-2">Details:</div>
                      <pre className="whitespace-pre-wrap text-xs">{event.details}</pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </AuthGuard>
  );
};

// Battle Simulation Class
class BattleSimulation {
  constructor(hero1, hero2, tempStrategies1 = {}, tempStrategies2 = {}) {
    this.hero1 = this.createBattleHero(hero1, tempStrategies1);
    this.hero2 = this.createBattleHero(hero2, tempStrategies2);
    this.log = [];
    this.turn = 0;
  }

  createBattleHero(heroData, tempStrategies) {
    return {
      id: heroData.id,
      name: heroData.name,
      level: heroData.level || 1,
      total_xp: heroData.total_xp || 0,
      // Attributes
      strength: heroData.strength || 10,
      endurance: heroData.endurance || 10,
      coordination: heroData.coordination || 10,
      quickness: heroData.quickness || 10,
      focus: heroData.focus || 10,
      self: heroData.self || 10,
      
      // Calculated stats
      maxHealth: 200 + (heroData.endurance * 2) + (heroData.strength * 1),
      health: 200 + (heroData.endurance * 2) + (heroData.strength * 1),
      maxStamina: (heroData.stats?.stamina ?? heroData.stamina ?? 100) || 100,
      stamina: (heroData.stats?.stamina ?? heroData.stamina ?? 100) || 100,
      moveSpeed: 20 + (heroData.quickness * 0.5),
      
      // Strategies (use temp overrides if provided)
      strategyAggression: tempStrategies.strategy_aggression ?? heroData.strategy_aggression ?? 50,
      strategyLooting: tempStrategies.strategy_looting ?? heroData.strategy_looting ?? 50,
      strategyPositioning: tempStrategies.strategy_positioning ?? heroData.strategy_positioning ?? 50,
      strategyMeleeRanged: tempStrategies.strategy_melee_ranged ?? heroData.strategy_melee_ranged ?? 50,
      strategyItemUse: tempStrategies.strategy_item_use ?? heroData.strategy_item_use ?? 50,
      
      // Equipment (default basic sword for now)
      equippedWeapon: {
        id: 'sword_basic',
        name: 'Basic Sword',
        type: 'melee',
        damage: { min: 5, max: 10 },
        attackSpeed: 1.0,
        durability: 100
      },
      equippedArmor: { head: null, chest: null, legs: null },
      
      // Stat modifiers
      statModifiers: {
        strength: 0, endurance: 0, coordination: 0,
        quickness: 0, focus: 0, self: 0,
        damage: 0, defense: 0, attackSpeed: 0
      },
      
      // Battle state
      isAlive: true,
      lastAttackTime: 0,
      damageDealt: 0,
      damageTaken: 0,
      eliminations: 0,
      attacksAttempted: 0,
      attacksHit: 0,
      criticalHits: 0,
      attacksMissed: 0,
      ultimateCharge: 0
    };
  }

  getStaminaRatio(hero) {
    const max = Math.max(0, hero.maxStamina || 0);
    if (!Number.isFinite(max) || max <= 0) return 1;
    const current = Math.max(0, hero.stamina || 0);
    return Math.max(0, Math.min(1, current / max));
  }

  getFatiguePenaltyScale(hero) {
    const ratio = this.getStaminaRatio(hero);
    if (ratio > 0.2) return 0;
    return Math.max(0, Math.min(1, (0.2 - ratio) / 0.2));
  }

  getFatigueHitBonus(hero) {
    const severity = this.getFatiguePenaltyScale(hero);
    if (severity <= 0) return 0;
    return severity * 0.2; // up to +20% hit chance when exhausted
  }

  getFatigueDamageMultiplier(hero) {
    const severity = this.getFatiguePenaltyScale(hero);
    if (severity <= 0) return 1;
    return 1 + severity * 1.0;
  }

  getEffectiveStrength(hero) {
    return hero.strength + hero.statModifiers.strength;
  }

  getEffectiveCoordination(hero) {
    return hero.coordination + hero.statModifiers.coordination;
  }

  getEffectiveQuickness(hero) {
    return hero.quickness + hero.statModifiers.quickness;
  }

  getEffectiveFocus(hero) {
    return hero.focus + hero.statModifiers.focus;
  }

  // Aggression calculation methods
  getAggressionDamageBonus(aggression) {
    // High aggression (80-100): +20% damage bonus
    // Medium aggression (40-60): 0% bonus
    // Low aggression (0-20): -10% damage penalty
    if (aggression >= 80) return (aggression - 80) * 1.0; // 0-20% bonus
    if (aggression <= 20) return (aggression - 20) * 0.5; // -10% to 0% penalty
    return 0; // 40-60: no change
  }

  getAggressionCritBonus(aggression) {
    // High aggression (80-100): +5% crit chance
    // Medium aggression (40-60): 0% bonus
    // Low aggression (0-20): -2% crit penalty
    if (aggression >= 80) return (aggression - 80) * 0.25; // 0-5% bonus
    if (aggression <= 20) return (aggression - 20) * 0.1; // -2% to 0% penalty
    return 0; // 40-60: no change
  }

  getAggressionEvasionBonus(aggression) {
    // High aggression (80-100): -5% evasion penalty
    // Medium aggression (40-60): 0% change
    // Low aggression (0-20): +5% evasion bonus
    if (aggression >= 80) return (aggression - 80) * -0.25; // 0 to -5% penalty
    if (aggression <= 20) return (20 - aggression) * 0.25; // 0 to +5% bonus
    return 0; // 40-60: no change
  }

  getAggressionCooldownModifier(aggression) {
    // High aggression (80-100): -200ms faster attacks
    // Medium aggression (40-60): 0ms change
    // Low aggression (0-20): +200ms slower attacks
    if (aggression >= 80) return (aggression - 80) * -10; // 0 to -200ms
    if (aggression <= 20) return (20 - aggression) * 10; // 0 to +200ms
    return 0; // 40-60: no change
  }

  getAggressionDefensePenalty(aggression) {
    // High aggression (80-100): -10% defense penalty
    // Medium aggression (40-60): 0% change
    // Low aggression (0-20): +10% defense bonus
    if (aggression >= 80) return (aggression - 80) * -0.5; // 0 to -10% penalty
    if (aggression <= 20) return (20 - aggression) * 0.5; // 0 to +10% bonus
    return 0; // 40-60: no change
  }

  calculateHitChance(attacker, defender) {
    // Base hit chance is 85%
    const baseHitChance = 0.85;
    
    // Accuracy from coordination (attacker)
    const accuracy = (this.getEffectiveCoordination(attacker) - 10) * 0.01; // 1% per point above 10
    
    // Evasion from quickness (defender) + aggression bonus/penalty
    const baseEvasion = (this.getEffectiveQuickness(defender) - 10) * 0.008; // 0.8% per point above 10
    const aggressionEvasionBonus = this.getAggressionEvasionBonus(defender.strategyAggression) / 100;
    const fatigueHitBonus = this.getFatigueHitBonus(defender);
    const evasion = baseEvasion + aggressionEvasionBonus;
    
    // Calculate final hit chance
    const hitChance = Math.max(0.05, Math.min(0.99, baseHitChance + accuracy - evasion + fatigueHitBonus)); // Cap
    
    return {
      baseHitChance,
      accuracy,
      baseEvasion,
      aggressionEvasionBonus,
      fatigueHitBonus,
      evasion,
      hitChance,
      details: `Base: ${(baseHitChance * 100).toFixed(1)}% + Accuracy: ${(accuracy * 100).toFixed(1)}% - Evasion: ${(baseEvasion * 100).toFixed(1)}% - Aggression Evasion: ${(aggressionEvasionBonus * 100).toFixed(1)}% + Fatigue Bonus: ${(fatigueHitBonus * 100).toFixed(1)}% = ${(hitChance * 100).toFixed(1)}%`
    };
  }

  calculateCriticalChance(attacker) {
    // Base critical chance is 5%
    const baseCritChance = 0.05;
    
    // Critical chance from focus
    const focusBonus = (this.getEffectiveFocus(attacker) - 10) * 0.003; // 0.3% per point above 10
    
    // Aggression critical bonus/penalty
    const aggressionBonus = this.getAggressionCritBonus(attacker.strategyAggression) / 100;
    
    const critChance = Math.min(0.25, baseCritChance + focusBonus + aggressionBonus); // Cap at 25%
    
    return {
      baseCritChance,
      focusBonus,
      aggressionBonus,
      critChance,
      details: `Base: ${(baseCritChance * 100).toFixed(1)}% + Focus: ${(focusBonus * 100).toFixed(1)}% + Aggression: ${(aggressionBonus * 100).toFixed(1)}% = ${(critChance * 100).toFixed(1)}%`
    };
  }

  calculateMeleeDamage(attacker, isCritical = false) {
    const weapon = attacker.equippedWeapon;
    const baseDamage = weapon.damage.min;
    const maxDamage = weapon.damage.max;
    
    const weaponDamage = baseDamage + Math.random() * (maxDamage - baseDamage);
    const strengthMultiplier = 1 + (this.getEffectiveStrength(attacker) - 10) * 0.005;
    const flatBonus = attacker.statModifiers.damage;
    
    // Apply aggression damage bonus/penalty
    const aggressionBonus = this.getAggressionDamageBonus(attacker.strategyAggression);
    const aggressionMultiplier = 1 + (aggressionBonus / 100);
    
    let finalDamage = (weaponDamage * strengthMultiplier) + flatBonus;
    finalDamage *= aggressionMultiplier;
    
    // Apply critical hit multiplier
    if (isCritical) {
      finalDamage *= 1.5; // 50% bonus damage on crit
    }
    
    return {
      weaponDamage,
      strengthMultiplier,
      flatBonus,
      aggressionBonus,
      aggressionMultiplier,
      isCritical,
      critMultiplier: isCritical ? 1.5 : 1.0,
      finalDamage,
      details: `Weapon: ${weaponDamage.toFixed(2)} × ${strengthMultiplier.toFixed(3)} + ${flatBonus} = ${((weaponDamage * strengthMultiplier) + flatBonus).toFixed(2)} × ${aggressionMultiplier.toFixed(3)} (Aggression) = ${(finalDamage / (isCritical ? 1.5 : 1)).toFixed(2)}${isCritical ? ` × 1.5 (Critical) = ${finalDamage.toFixed(2)}` : ''}`
    };
  }

  calculateDamageReduction(defender, incomingDamage) {
    const coordReduction = (this.getEffectiveCoordination(defender) - 10) * 0.005;
    let armorDefense = 0;
    if (defender.equippedArmor.head) armorDefense += defender.equippedArmor.head.defense || 0;
    if (defender.equippedArmor.chest) armorDefense += defender.equippedArmor.chest.defense || 0;
    if (defender.equippedArmor.legs) armorDefense += defender.equippedArmor.legs.defense || 0;
    
    // Aggression defense bonus/penalty
    const aggressionDefenseBonus = this.getAggressionDefensePenalty(defender.strategyAggression) / 100;
    
    const defenseBonus = defender.statModifiers.defense + armorDefense;
    const totalReduction = Math.min(0.75, coordReduction + defenseBonus + aggressionDefenseBonus);
    let finalDamage = incomingDamage * (1 - totalReduction);
    const fatigueMultiplier = this.getFatigueDamageMultiplier(defender);
    if (fatigueMultiplier > 1) {
      finalDamage *= fatigueMultiplier;
    }
    
    return {
      coordReduction,
      armorDefense,
      defenseBonus,
      aggressionDefenseBonus,
      totalReduction,
      finalDamage,
      details: `Coordination: ${coordReduction.toFixed(3)} + Armor: ${armorDefense} + Bonus: ${defender.statModifiers.defense} + Aggression: ${aggressionDefenseBonus.toFixed(3)} = ${totalReduction.toFixed(3)} reduction\n${incomingDamage.toFixed(2)} × (1 - ${totalReduction.toFixed(3)}) = ${(incomingDamage * (1 - totalReduction)).toFixed(2)}${fatigueMultiplier > 1 ? ` × Fatigue ${fatigueMultiplier.toFixed(2)} = ${finalDamage.toFixed(2)}` : ''}`
    };
  }

  getAttackCooldownMs(hero) {
    const base = 2000; // Increased base cooldown to 2 seconds
    const weapon = hero.equippedWeapon;
    let adjustedBase = base / weapon.attackSpeed;
    
    const quicknessReduction = (this.getEffectiveQuickness(hero) - 10) * 0.003; // Reduced from 0.005 to 0.003
    const itemSpeedBonus = hero.statModifiers.attackSpeed || 0;
    
    const totalReduction = Math.min(0.4, quicknessReduction + itemSpeedBonus); // Reduced max reduction from 0.5 to 0.4
    let cooldown = adjustedBase * (1 - totalReduction);
    
    // Apply aggression modifier (can be positive or negative)
    cooldown += this.getAggressionCooldownModifier(hero.strategyAggression);
    cooldown = Math.max(800, cooldown); // Increased minimum from 200ms to 800ms
    
    return {
      base,
      adjustedBase,
      quicknessReduction,
      itemSpeedBonus,
      aggressionModifier: this.getAggressionCooldownModifier(hero.strategyAggression),
      totalReduction,
      cooldown,
      details: `Base: ${base}ms ÷ ${weapon.attackSpeed} = ${adjustedBase.toFixed(0)}ms\nQuickness: ${quicknessReduction.toFixed(3)} + Speed Bonus: ${itemSpeedBonus} = ${totalReduction.toFixed(3)} reduction\n${adjustedBase.toFixed(0)} × (1 - ${totalReduction.toFixed(3)}) = ${(adjustedBase * (1 - totalReduction)).toFixed(0)}ms + Aggression: ${this.getAggressionCooldownModifier(hero.strategyAggression)}ms = ${cooldown.toFixed(0)}ms`
    };
  }

  takeDamage(hero, amount) {
    if (!hero.isAlive) return 0;
    
    const reduction = this.calculateDamageReduction(hero, amount);
    hero.health -= reduction.finalDamage;
    hero.damageTaken += reduction.finalDamage;
    hero.ultimateCharge = Math.min(100, (hero.ultimateCharge || 0) + 0.5);
    
    if (hero.health <= 0) {
      hero.health = 0;
      hero.isAlive = false;
    }
    
    return reduction;
  }

  logEvent(summary, details) {
    this.log.push({
      timestamp: `Turn ${this.turn}`,
      summary,
      details
    });
  }

  generateFinalStats(battleDurationSeconds = null) {
    const hero1Stats = this.generateHeroStats(this.hero1, battleDurationSeconds);
    const hero2Stats = this.generateHeroStats(this.hero2, battleDurationSeconds);
    
    return `${hero1Stats}\n\n${hero2Stats}`;
  }

  generateHeroStats(hero, battleDurationSeconds = null) {
    const hitRate = hero.attacksAttempted > 0 ? (hero.attacksHit / hero.attacksAttempted * 100).toFixed(1) : '0.0';
    const critRate = hero.attacksHit > 0 ? (hero.criticalHits / hero.attacksHit * 100).toFixed(1) : '0.0';
    const avgDamage = hero.attacksHit > 0 ? (hero.damageDealt / hero.attacksHit).toFixed(1) : '0.0';
    
    let stats = `${hero.name} (Level ${hero.level}):
• Final Health: ${hero.health.toFixed(1)}/${hero.maxHealth} (${((hero.health / hero.maxHealth) * 100).toFixed(1)}%)
• Attacks: ${hero.attacksAttempted} attempted, ${hero.attacksHit} hit, ${hero.attacksMissed} missed
• Hit Rate: ${hitRate}% | Critical Hits: ${hero.criticalHits} (${critRate}% of hits)
• Damage: ${hero.damageDealt.toFixed(1)} dealt, ${hero.damageTaken.toFixed(1)} taken
• Average Damage per Hit: ${avgDamage}
• Eliminations: ${hero.eliminations}`;

    // Add real-time specific stats if battle duration is provided
    if (battleDurationSeconds && battleDurationSeconds > 0) {
      const attacksPerSecond = (hero.attacksAttempted / battleDurationSeconds).toFixed(2);
      const damagePerSecond = (hero.damageDealt / battleDurationSeconds).toFixed(1);
      stats += `\n• Attacks per Second: ${attacksPerSecond} | Damage per Second: ${damagePerSecond}`;
    }
    
    return stats;
  }

  runBattle() {
    this.logEvent(
      `Battle begins: ${this.hero1.name} (Level ${this.hero1.level || 1}) vs ${this.hero2.name} (Level ${this.hero2.level || 1})`,
      `Hero 1: ${this.hero1.name} (Level ${this.hero1.level || 1}, Health: ${this.hero1.maxHealth}, Strength: ${this.hero1.strength}, Coordination: ${this.hero1.coordination}, Quickness: ${this.hero1.quickness})\nHero 2: ${this.hero2.name} (Level ${this.hero2.level || 1}, Health: ${this.hero2.maxHealth}, Strength: ${this.hero2.strength}, Coordination: ${this.hero2.coordination}, Quickness: ${this.hero2.quickness})`
    );

    // Debug: Log initial hero states
    this.logEvent(
      `Debug: Initial hero states`,
      `Hero 1 - Alive: ${this.hero1.isAlive}, Health: ${this.hero1.health}/${this.hero1.maxHealth}\nHero 2 - Alive: ${this.hero2.isAlive}, Health: ${this.hero2.health}/${this.hero2.maxHealth}`
    );

    const maxTurns = 100; // Prevent infinite battles
    
    while (this.hero1.isAlive && this.hero2.isAlive && this.turn < maxTurns) {
      this.turn++;
      
      try {
        // Determine attack order based on quickness
        const hero1Cooldown = this.getAttackCooldownMs(this.hero1);
        const hero2Cooldown = this.getAttackCooldownMs(this.hero2);
        
        // Debug: Log cooldown info
        this.logEvent(
          `Debug: Turn ${this.turn} cooldowns`,
          `Hero 1 cooldown: ${hero1Cooldown.cooldown.toFixed(0)}ms\nHero 2 cooldown: ${hero2Cooldown.cooldown.toFixed(0)}ms`
        );
        
        // Hero with lower cooldown attacks first
        if (hero1Cooldown.cooldown <= hero2Cooldown.cooldown) {
          this.processAttack(this.hero1, this.hero2, hero1Cooldown);
          if (this.hero2.isAlive) {
            this.processAttack(this.hero2, this.hero1, hero2Cooldown);
          }
        } else {
          this.processAttack(this.hero2, this.hero1, hero2Cooldown);
          if (this.hero1.isAlive) {
            this.processAttack(this.hero1, this.hero2, hero1Cooldown);
          }
        }
      } catch (error) {
        console.error('Error in battle turn:', error);
        this.logEvent(
          `Battle error on turn ${this.turn}`,
          `Error: ${error.message}`
        );
        break;
      }
    }

    // Battle conclusion
    if (this.hero1.isAlive && this.hero2.isAlive) {
      this.logEvent(
        `Battle ended in a draw after ${this.turn} turns`,
        `Both heroes survived the maximum number of turns`
      );
    } else if (this.hero1.isAlive) {
      this.logEvent(
        `${this.hero1.name} wins!`,
        `Final Health: ${this.hero1.health.toFixed(1)}/${this.hero1.maxHealth}\nDamage Dealt: ${this.hero1.damageDealt.toFixed(1)}\nDamage Taken: ${this.hero1.damageTaken.toFixed(1)}`
      );
    } else {
      this.logEvent(
        `${this.hero2.name} wins!`,
        `Final Health: ${this.hero2.health.toFixed(1)}/${this.hero2.maxHealth}\nDamage Dealt: ${this.hero2.damageDealt.toFixed(1)}\nDamage Taken: ${this.hero2.damageTaken.toFixed(1)}`
      );
    }

    // Add final stats summary
    this.logEvent(
      "=== FINAL BATTLE STATISTICS ===",
      this.generateFinalStats()
    );

    return this.log;
  }

  processAttack(attacker, defender, cooldownInfo) {
    // Track attack attempt
    attacker.attacksAttempted++;
    
    // Calculate hit chance
    const hitInfo = this.calculateHitChance(attacker, defender);
    const hitRoll = Math.random();
    const isHit = hitRoll < hitInfo.hitChance;
    
    if (!isHit) {
      // Miss - no damage dealt
      attacker.attacksMissed++;
      this.logEvent(
        `${attacker.name} attacks ${defender.name} but misses!`,
        `Attack Cooldown: ${cooldownInfo.details}\n\nHit Chance: ${hitInfo.details}\n\nHit Roll: ${(hitRoll * 100).toFixed(1)}% (needed ${(hitInfo.hitChance * 100).toFixed(1)}%)`
      );
      return;
    }
    
    // Track successful hit
    attacker.attacksHit++;
    
    // Calculate critical hit chance
    const critInfo = this.calculateCriticalChance(attacker);
    const critRoll = Math.random();
    const isCritical = critRoll < critInfo.critChance;
    
    // Track critical hit
    if (isCritical) {
      attacker.criticalHits++;
    }
    
    // Calculate damage
    const damageInfo = this.calculateMeleeDamage(attacker, isCritical);
    const reductionInfo = this.takeDamage(defender, damageInfo.finalDamage);
    
    attacker.damageDealt += reductionInfo.finalDamage;
    attacker.ultimateCharge = Math.min(100, (attacker.ultimateCharge || 0) + 0.5); // mirror in-sim behavior
    
    // Create attack summary
    const attackType = isCritical ? " critically hits" : " hits";
    const critText = isCritical ? " (CRITICAL HIT!)" : "";
    
    this.logEvent(
      `${attacker.name}${attackType} ${defender.name} for ${reductionInfo.finalDamage.toFixed(1)} damage${critText}`,
      `Attack Cooldown: ${cooldownInfo.details}\n\nHit Chance: ${hitInfo.details}\nHit Roll: ${(hitRoll * 100).toFixed(1)}% (success)\n\nCritical Chance: ${critInfo.details}\nCrit Roll: ${(critRoll * 100).toFixed(1)}%${isCritical ? ' (CRITICAL!)' : ' (normal)'}\n\nDamage Calculation: ${damageInfo.details}\n\nDefense Calculation: ${reductionInfo.details}\n\nDefender Health: ${defender.health.toFixed(1)}/${defender.maxHealth}`
    );

    if (!defender.isAlive) {
      attacker.eliminations++;
      this.logEvent(
        `${defender.name} has been defeated!`,
        `${attacker.name} gains an elimination`
      );
    }
  }
}

export default BattleSimulator;